import { useState, useContext } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { LogIn, Info, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext } from '../../contexts/AuthContext';
import { auth, db } from '../../firebase/config';
import { handleFirebaseError } from '../../utils/errorHandler';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import SEO from '../SEO';

const isEmailLike = (v) => /@/.test(v);

const Login = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useContext(AuthContext);

  const from = location.state?.from?.pathname || '/tournaments';

  const resolveLegacyEmail = async (username) => {
    const snap = await getDocs(
      query(collection(db, 'userProfiles'), where('username', '==', username))
    );
    if (snap.empty) return null;
    const data = snap.docs[0].data();
    if (data.email) return data.email;
    return data.generatedEmail || null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const id = identifier.trim();
    const pwd = password.trim();
    if (!id || !pwd) {
      setError('Enter your email (or username) and password.');
      return;
    }

    setLoading(true);
    try {
      let emailToUse = id;
      if (!isEmailLike(id)) {
        const legacy = await resolveLegacyEmail(id);
        if (!legacy) {
          throw Object.assign(new Error('No account found with this username.'), {
            code: 'auth/user-not-found',
          });
        }
        emailToUse = legacy;
      }

      await signInWithEmailAndPassword(auth, emailToUse, pwd);
      toast.success('Welcome back.');
      setTimeout(() => {
        navigate(isAdmin() ? '/admin/dashboard' : from, { replace: true });
      }, 200);
    } catch (err) {
      console.error('Login error:', err);
      const msg =
        err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password'
          ? 'Invalid credentials. Please try again.'
          : handleFirebaseError(err);
      setError(msg);
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-md flex-col justify-center px-4 py-12 sm:px-6">
      <SEO title="Log In" canonicalPath="/login" noindex />
      <div className="mb-8 flex flex-col gap-2">
        <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          <span className="h-px w-6 bg-primary" />
          Account
        </div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Log in</h1>
        <p className="text-sm text-muted-foreground">
          Use your email to sign in. New here?{' '}
          <Link to="/signup" className="text-foreground underline underline-offset-4">
            Create an account
          </Link>
          .
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-5 rounded-lg border border-border/60 bg-card p-6 shadow-sm"
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="identifier">Email</Label>
          <Input
            id="identifier"
            type="text"
            autoComplete="username"
            inputMode="email"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            disabled={loading}
            placeholder="you@example.com"
          />
          <p className="text-xs text-muted-foreground">
            Legacy accounts can also enter their username.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}

        <Button type="submit" disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
          {loading ? 'Signing in…' : 'Log in'}
        </Button>

        <div className="flex items-start gap-2 rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-info" />
          <span>
            Username sign-in is being phased out. After logging in, add your email in{' '}
            <span className="text-foreground">Account settings</span>.
          </span>
        </div>
      </form>
    </div>
  );
};

export default Login;
